/**
 * MetaLeadsStatusCard — la integración Meta Lead Ads de un vistazo (admin).
 *
 * Enseña si está encendida, si le falta configuración, cuándo llegó el
 * último aviso de Meta, qué hay pendiente o fallido y los últimos avisos
 * (ids y estados, nunca datos del lead). «Comprobar con Meta» pregunta a
 * Meta, sin cambiar nada allí, si el token, la página y la suscripción están
 * bien; «Reintentar» vuelve a poner en cola lo que falló (después de
 * arreglar el token o los permisos) y lo que se ignoró por su página si esa
 * página ya está admitida, con confirmación.
 *
 * Los interruptores y los secretos viven en el entorno del servidor: esta
 * tarjeta no los enciende ni los enseña. Se relee cada 30 s mientras la
 * pestaña está a la vista, y si una relectura falla lo dice en vez de seguir
 * enseñando el último estado como si fuera el actual.
 */
import React, { useCallback, useEffect, useState } from "react";
import { formatError } from "@/lib/api";
import {
  checkMetaLeads,
  getMetaLeadsEvents,
  getMetaLeadsStatus,
  retryAllMetaLeads,
} from "@/lib/metaLeadsApi";
import { CheckCircle, Warning, XCircle, MinusCircle, ArrowsClockwise, ArrowCounterClockwise } from "@phosphor-icons/react";

const REFRESCO_MS = 30000;
const TAP = "min-h-[44px]";

const SALUD = {
  ok: { label: "Funcionando", Icon: CheckCircle, cls: "text-emerald-700 dark:text-emerald-400" },
  attention: { label: "Revisar", Icon: Warning, cls: "text-amber-700 dark:text-amber-400" },
  down: { label: "Interrumpida", Icon: XCircle, cls: "text-red-700 dark:text-red-300" },
  off: { label: "Apagada", Icon: MinusCircle, cls: "text-stone-500" },
};

const PROBLEMAS = {
  webhook_not_configured: "Falta el App Secret o el identificador de verificación en el servidor.",
  processing_not_configured: "Falta el token, el App Secret o las páginas admitidas en el servidor.",
  integration_error: "Meta rechaza el token o los permisos (o falta el acceso a los leads).",
  signature_failures: "Llegan avisos con forma de Meta y una firma que no cuadra (App Secret distinto).",
  page_not_allowed: "Llegan avisos de una página que no está admitida (o del botón «Probar»).",
  ignored_now_allowed: "Hay avisos ignorados de una página que ya está admitida: pulsa «Reintentar».",
  processing_disabled_backlog: "Hay leads esperando y el alta está apagada en el servidor.",
  stuck_pending: "Hay avisos esperando más de 30 minutos.",
  silence: "No llega ningún aviso de Meta desde hace demasiado.",
  failed_rows: "Hay leads que no se pudieron leer o dar de alta.",
};

const ESTADOS = {
  received: "Recibido",
  processing: "Procesando",
  retry: "Reintentando",
  created: "Lead creado",
  merged: "Unido a un lead",
  failed: "Fallido",
  ignored: "Ignorado",
};

function fecha(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function SiNo({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <CheckCircle size={16} aria-hidden="true" className="text-emerald-700 dark:text-emerald-400 shrink-0" />
      ) : (
        <MinusCircle size={16} aria-hidden="true" className="text-stone-400 shrink-0" />
      )}
      <span className={ok ? "" : "text-stone-500"}>
        {label}: {ok ? "sí" : "no"}
      </span>
    </div>
  );
}

function detalle(a) {
  const partes = [];
  if (a.last_error?.code) partes.push(`Error ${a.last_error.code}`);
  else if (a.last_error?.kind) partes.push(a.last_error.kind);
  const wa = [a.first_contact?.outcome, a.first_contact?.reason].filter(Boolean).join(" · ");
  if (wa) partes.push(`WhatsApp: ${wa}`);
  if (a.ignored_reason) partes.push(a.ignored_reason);
  if (a.warnings?.length) partes.push(a.warnings.join(", "));
  if (a.disclaimer_keys?.length) partes.push(`casillas: ${a.disclaimer_keys.join(", ")}`);
  return partes.join(" · ");
}

export default function MetaLeadsStatusCard() {
  const [estado, setEstado] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [error, setError] = useState(null);
  const [leidoEn, setLeidoEn] = useState(null);
  const [comprobacion, setComprobacion] = useState(null);
  const [comprobando, setComprobando] = useState(false);
  const [reintento, setReintento] = useState(null);
  const [reintentando, setReintentando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([getMetaLeadsStatus(), getMetaLeadsEvents(10)]);
      setEstado(s);
      setAvisos(e);
      setError(null);
      setLeidoEn(new Date().toISOString());
    } catch (err) {
      setError(formatError(err));
    }
  }, []);

  useEffect(() => {
    cargar();
    const t = setInterval(() => {
      if (document.visibilityState !== "hidden") cargar();
    }, REFRESCO_MS);
    const alVolver = () => {
      if (document.visibilityState === "visible") cargar();
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [cargar]);

  const comprobar = async () => {
    setComprobando(true);
    try {
      setComprobacion(await checkMetaLeads());
    } catch (err) {
      setComprobacion({ error: formatError(err) });
    } finally {
      setComprobando(false);
    }
  };

  const reintentar = async () => {
    if (
      !window.confirm(
        "¿Volver a poner en cola los leads de Meta fallidos, y los ignorados de una página que ya está admitida?"
      )
    )
      return;
    setReintentando(true);
    try {
      const r = await retryAllMetaLeads();
      setReintento(`En cola: ${r.failed_requeued ?? 0} fallidos y ${r.ignored_requeued ?? 0} ignorados.`);
      await cargar();
    } catch (err) {
      setReintento(formatError(err));
    } finally {
      setReintentando(false);
    }
  };

  if (!estado) {
    return (
      <div className="dv-card text-sm" data-testid="meta-leads-card">
        {error ? (
          <div className="text-red-700 dark:text-red-300" role="alert" data-testid="meta-leads-error">
            {error}
          </div>
        ) : (
          <div className="text-stone-500">Cargando…</div>
        )}
      </div>
    );
  }

  const salud = SALUD[estado.health] || SALUD.off;
  const cfg = estado.config || {};
  const libro = estado.ledger || {};
  const reintentables = (libro.failed || 0) + (libro.ignored_requeueable || 0);

  return (
    <div className="dv-card space-y-5" data-testid="meta-leads-card">
      {error && (
        <div
          className="text-sm rounded border border-red-200 bg-red-50 text-red-800 dark:bg-red-950/40 dark:border-red-800 dark:text-red-200 px-3 py-2"
          role="alert"
          data-testid="meta-leads-stale"
        >
          No se pudo actualizar el estado ({error}). Lo que ves es de {fecha(leidoEn)}.
        </div>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className={`flex items-center gap-2 font-medium ${salud.cls}`} data-testid="meta-leads-health">
          <salud.Icon size={20} weight="fill" aria-hidden="true" />
          {salud.label}
        </div>
        <div className="flex gap-2 flex-wrap">
          {reintentables > 0 && (
            <button
              type="button"
              className={`dv-btn dv-btn-ghost ${TAP} inline-flex items-center gap-2`}
              onClick={reintentar}
              disabled={reintentando}
              data-testid="meta-leads-retry"
            >
              <ArrowCounterClockwise size={16} aria-hidden="true" />
              {reintentando ? "Reintentando…" : `Reintentar (${reintentables})`}
            </button>
          )}
          <button
            type="button"
            className={`dv-btn dv-btn-ghost ${TAP} inline-flex items-center gap-2`}
            onClick={comprobar}
            disabled={comprobando || !cfg.access_token_set}
            data-testid="meta-leads-check"
          >
            <ArrowsClockwise size={16} aria-hidden="true" />
            {comprobando ? "Comprobando…" : "Comprobar con Meta"}
          </button>
        </div>
      </div>

      {estado.issues?.length > 0 && (
        <ul className="space-y-1" data-testid="meta-leads-issues">
          {estado.issues.map((p) => (
            <li key={p} className="text-sm text-amber-800 dark:text-amber-300 flex gap-2">
              <Warning size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
              {PROBLEMAS[p] || p}
            </li>
          ))}
        </ul>
      )}

      {reintento && (
        <div className="text-sm text-stone-600 dark:text-stone-300" role="status">
          {reintento}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-2">
        <SiNo ok={cfg.webhook_enabled} label="Webhook encendido" />
        <SiNo ok={cfg.processing_enabled} label="Alta de leads encendida" />
        <SiNo ok={cfg.app_secret_set && cfg.verify_token_set} label="Firma y verificación configuradas" />
        <SiNo ok={cfg.access_token_set && cfg.page_ids_count > 0} label="Token y página configurados" />
        <SiNo
          ok={cfg.first_contact_enabled}
          label={`Primer WhatsApp automático (${
            cfg.first_contact_audience === "all"
              ? "todos"
              : cfg.first_contact_audience === "next"
                ? `piloto: ${cfg.first_contact_allowlist_count ?? 0} teléfono(s) y el próximo lead`
                : `piloto: ${cfg.first_contact_allowlist_count ?? 0} teléfono(s)`
          })`}
        />
        <SiNo ok={cfg.consent_keys_count > 0} label="Casilla de consentimiento configurada" />
        <SiNo
          ok={cfg.consent_by_submission_forms_count > 0}
          label={`Formularios cuyo envío es el permiso: ${cfg.consent_by_submission_forms_count ?? 0}`}
        />
      </div>

      <div className="grid sm:grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Último aviso</div>
          <div>{fecha(libro.last_notification_at)}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Pendientes · fallidos</div>
          <div>
            {libro.pending ?? 0} · {libro.failed ?? 0}
          </div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Leads en 7 días</div>
          <div>
            {libro.created_7d ?? 0} nuevos · {libro.merged_7d ?? 0} unidos
          </div>
        </div>
      </div>

      {avisos.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="meta-leads-events">
            <thead>
              <tr className="text-left text-stone-500">
                <th className="py-1 pr-3 font-normal">Recibido</th>
                <th className="py-1 pr-3 font-normal">Lead de Meta</th>
                <th className="py-1 pr-3 font-normal">Estado</th>
                <th className="py-1 font-normal">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {avisos.map((a) => (
                <tr key={a.leadgen_id} className="border-t border-stone-200 dark:border-stone-800">
                  <td className="py-1 pr-3 whitespace-nowrap">{fecha(a.received_at)}</td>
                  <td className="py-1 pr-3 font-mono">{a.leadgen_id}</td>
                  <td className="py-1 pr-3">
                    {ESTADOS[a.status] || a.status}
                    {a.test ? " · prueba" : ""}
                    {a.origin === "backfill" ? " · recuperado" : ""}
                  </td>
                  <td className="py-1 text-stone-500">{detalle(a)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {comprobacion && (
        <div
          className="text-xs bg-stone-50 dark:bg-stone-900 rounded p-3 space-y-1"
          role="status"
          data-testid="meta-leads-check-result"
        >
          {comprobacion.error ? (
            <div className="text-red-700 dark:text-red-300">{comprobacion.error}</div>
          ) : (
            Object.entries(comprobacion.checks || {}).map(([k, v]) => (
              <div key={k} className="flex gap-2 flex-wrap">
                <span className="font-mono">{k}</span>
                <span>{v?.ok === true ? "correcto" : v?.ok === false ? "falla" : "sin dato"}</span>
                {v?.error?.code ? <span className="text-stone-500">código {v.error.code}</span> : null}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
